import React from 'react'
import { Grid, Timeline, Popover } from '@enterprise-ui/canvas-ui-react'
import '../Custom.css'
import { AuditEntryInput } from '../Common/Queries'
import { DateFormatter } from '@enterprise-ui/canvas-ui-react-date'
import memoize from 'memoize-one'
import moment from 'moment'
import _ from 'lodash'
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

const activityColor = css`
  color: #009999;
`

const iconStyle = css`
  cursor: pointer;
  margin-left: 8px;
  color: #0066cc;
  font-size: 18px;
  font-weight: bold;
  display: inline-block;
  &:hover {
    color: #003399;
  }
`

interface Props {
  auditEntries: AuditEntryInput[]
  caseData?: any
}

const AuditLog: React.FC<Props> = ({ auditEntries, caseData }) => {
  const formatDataForRendering = memoize((auditEntriesValues) => {
    let convertedData = _.groupBy(
      _.orderBy(auditEntriesValues, ['activityTimestamp'], ['desc']),
      (auditEntriesValues) => {
        return moment(auditEntriesValues.activityTimestamp).format('YYYY-MM-DD')
      },
    )
    return convertedData
  })

  const formattedData = formatDataForRendering(auditEntries)
  return (
    <div className="hc-pa-normal hc-pa-none">
      <Timeline>
        {Object.keys(formattedData).map((event) => {
          return (
            <React.Fragment key={event}>
              <Timeline.Flag>
                <DateFormatter date={event} size="expanded" relative />
              </Timeline.Flag>
              {formattedData[event].map((row: any, i: any) => {
                const isReopenActivity = row.activityDescription
                  ?.toLowerCase()
                  .includes('reopened')
                const reopenDataArray = caseData?.reopenDetails || []
                // Find the matching reopen entry by closest reopenDate to activity timestamp
                let reopenData: any = null
                if (isReopenActivity && reopenDataArray.length > 0) {
                  const activityTime = new Date(row.activityTimestamp).getTime()
                  reopenData = reopenDataArray.reduce(
                    (closest: any, entry: any) => {
                      if (!entry?.reopenDate) return closest
                      const entryTime = new Date(entry.reopenDate).getTime()
                      if (!closest) return entry
                      const closestTime = new Date(closest.reopenDate).getTime()
                      return Math.abs(entryTime - activityTime) <
                        Math.abs(closestTime - activityTime)
                        ? entry
                        : closest
                    },
                    null,
                  )
                }
                const hasReopenData =
                  isReopenActivity &&
                  reopenData &&
                  Object.keys(reopenData).length > 0

                return (
                  <Timeline.Item
                    key={`${event}-${row?.activityTimestamp ?? i}-${row?.userId ?? 'user'}-${i}`}
                    type="success"
                  >
                    <Grid.Container>
                      <Grid.Item>
                        <p className="hc-clr-grey02 hc-fs-xs">
                          <DateFormatter
                            format="MM/DD/YYYY hh:mm A"
                            timezone="America/Chicago"
                            date={row?.activityTimestamp.concat('Z')}
                          />
                          &nbsp;&nbsp;&mdash;
                        </p>
                      </Grid.Item>
                      <Grid.Item className="hc-pr-none">
                        <p className="hc-fs-sm" css={activityColor}>
                          {row.activityDescription}
                        </p>
                      </Grid.Item>
                      {hasReopenData && (
                        <Grid.Item
                          className="hc-pl-none hc-pr-none"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          <Popover
                            content={
                              <div style={{ maxWidth: '350px' }}>
                                <p
                                  style={{
                                    marginBottom: '10px',
                                    fontWeight: 'bold',
                                  }}
                                >
                                  Reopen Details
                                </p>
                                <div
                                  style={{
                                    fontSize: '12px',
                                    lineHeight: '1.6',
                                  }}
                                >
                                  <p>
                                    <strong>Reopen Reason:</strong>{' '}
                                    {reopenData?.reopenReason || 'N/A'}
                                  </p>
                                  <p>
                                    <strong>Reopen Date:</strong>{' '}
                                    {reopenData?.reopenDate ? (
                                      <DateFormatter
                                        date={reopenData?.reopenDate}
                                        format="L hh:mm A"
                                      />
                                    ) : (
                                      'N/A'
                                    )}
                                  </p>
                                  <p>
                                    <strong>Reopen User:</strong>{' '}
                                    {reopenData?.reopenUser || 'N/A'}
                                  </p>
                                  <p>
                                    <strong>Previous Resolution Code:</strong>{' '}
                                    {reopenData?.previousResolutionCodeText ||
                                      'N/A'}
                                  </p>
                                  <p>
                                    <strong>Previous Resolution Amount:</strong>{' '}
                                    {reopenData?.previousResolutionAmount ||
                                      'N/A'}
                                  </p>
                                  <p>
                                    <strong>Previous Resolution Date:</strong>{' '}
                                    {reopenData?.previousResolutionDate ? (
                                      <DateFormatter
                                        date={
                                          reopenData?.previousResolutionDate
                                        }
                                        format="L"
                                      />
                                    ) : (
                                      'N/A'
                                    )}
                                  </p>
                                  <p>
                                    <strong>Previous Resolution Text:</strong>{' '}
                                    {reopenData?.previousResolutionText ||
                                      'N/A'}
                                  </p>
                                  {reopenData?.lastResolutionComment && (
                                    <p>
                                      <strong>Last Resolution Comment:</strong>{' '}
                                      {reopenData.lastResolutionComment}
                                    </p>
                                  )}
                                  {reopenData?.justificationForReopen && (
                                    <p>
                                      <strong>Justification for Reopen:</strong>{' '}
                                      {reopenData.justificationForReopen}
                                    </p>
                                  )}
                                </div>
                              </div>
                            }
                            location="right"
                            type="clickOnly"
                          >
                            <span css={iconStyle}>ⓘ</span>
                          </Popover>
                        </Grid.Item>
                      )}
                      <Grid.Item className="hc-pl-none hc-pr-none">
                        <p className="hc-clr-grey01 hc-fs-sm">
                          &nbsp;{'by'}&nbsp;
                        </p>
                      </Grid.Item>
                      <Grid.Item className="hc-pl-none">
                        <p css={activityColor}>{row.userId}</p>
                      </Grid.Item>
                    </Grid.Container>
                  </Timeline.Item>
                )
              })}
            </React.Fragment>
          )
        })}
      </Timeline>
    </div>
  )
}
export default AuditLog
